import { object, bool, string, number, func } from "prop-types";
import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { twMerge } from "tailwind-merge";

import api from "@api/index";

import UpsellItemDetail from "./UpsellItemDetail";
import ServiceTile from "@components/Customize/shared/ServiceTile";
import FinancePrice from "@components/shared/FinancePrice";
import InfoIcon from "@components/shared/Icons/InfoIcon";
import Modal from "@components/shared/Modal";

import gtm from "@gtm/core";
import {
  changeConfigurationEvent,
  changeSelectionEvent,
} from "@gtm/events/homepage";

import { useIsCashJourneySelected } from "@hooks/useIsCashJourneySelected";

import { getIsPreconfigAvailable } from "@lib/getIsPreconfigAvailable";
import { getStaticAssetURL } from "@lib/getStaticAssetURL";
import { toggleElementInArray } from "@lib/toggleElementInArray";

import {
  removePromoCodeAction,
  updateCarConfigurationAction,
  updateCarPreconfigurationAction,
} from "@redux/actions/dealActions";
import { setLastChangedAtAction } from "@redux/actions/deprecatedActions";
import { usePreconfigurationsSelector } from "@redux/reducers/appConfig";
import { useIsUserTrustedSelector } from "@redux/reducers/auth";
import {
  useBusinessModelSelector,
  useCarConfigurationSelector,
} from "@redux/reducers/deal";

import { CUSTOMIZE_TYPES, SESSION_STORAGE_KEYS } from "@shared/constants";
import { getAccessoryImagePath } from "@shared/v2/lib/getAccessoryImagePath";
import { getCarImagePath } from "@shared/v2/lib/getCarImagePath";

import { sessionStorageClient } from "utils/sessionStorageClient";

const UpsellCarouselItem = props => {
  const { index, item, isReadOnly, isSelected, setIsWorking, type } = props;
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const preconfigurations = usePreconfigurationsSelector();
  const configuration = useCarConfigurationSelector();
  const dispatch = useDispatch();
  const isUserTrusted = useIsUserTrustedSelector();

  const businessModel = useBusinessModelSelector();
  const isCashJourney = useIsCashJourneySelected();

  const selectedItems = configuration[type] ?? [];

  const isAccessory = type === CUSTOMIZE_TYPES.ACCESSORY;
  const isPack = type === CUSTOMIZE_TYPES.PACK;
  const isService = type === CUSTOMIZE_TYPES.SERVICE;

  const isBasePreconfigAvailable = useMemo(() => {
    if (isPack) {
      return getIsPreconfigAvailable(
        item.basePreconfigStatus,
        businessModel,
        isCashJourney
      );
    }

    return true;
  }, [item, businessModel, isCashJourney]);

  const image = useMemo(() => {
    if (isPack) {
      return getStaticAssetURL(getCarImagePath(item.id));
    }
    if (isAccessory) {
      return getStaticAssetURL(getAccessoryImagePath(item.officialReference));
    }

    return null;
  }, [props]);

  const label = useMemo(() => {
    if (isPack) {
      return item.pack.label;
    }
    return item.label;
  }, [props]);

  const price = useMemo(() => {
    let netPrice = item.netPrice;

    if (isPack) {
      netPrice = item.pack.netPrice;
    }

    return netPrice;
  }, [item, type]);

  const handleSelect = async () => {
    if (isReadOnly) {
      return;
    }

    try {
      setIsWorking(true);

      if (isAccessory) {
        dispatch(
          updateCarConfigurationAction({
            [type]: toggleElementInArray(selectedItems, item),
          })
        );
      } else if (isService) {
        const isRemove = selectedItems.some(({ id }) => id === item.id);

        dispatch(
          updateCarConfigurationAction({ [type]: isRemove ? [] : [item] })
        );
      } else if (isPack) {
        if (!isBasePreconfigAvailable && isSelected) {
          // Let's check if the other financing journey of base preconfig is available
          // so current one can get deselected and journey is autoswitched
          if (
            !getIsPreconfigAvailable(
              item.basePreconfigStatus,
              businessModel,
              !isCashJourney
            )
          ) {
            return;
          }
        }

        const preconfiguration = isSelected
          ? preconfigurations.find(p => p.id === item.basePreconfigId)
          : item;

        if (!isUserTrusted) {
          api.removePromoCodeFromSession();
        }

        dispatch(removePromoCodeAction());
        dispatch(updateCarPreconfigurationAction(preconfiguration));
        dispatch(setLastChangedAtAction(Date.now()));

        sessionStorageClient.removeItem(SESSION_STORAGE_KEYS.PROMOCODE);

        gtm.fire(changeConfigurationEvent(preconfiguration.label));
      }

      // GTM for preconfig was done above
      if (isAccessory || isService) {
        gtm.fire(
          changeSelectionEvent({
            action: isSelected ? "Remove" : "Add",
            label,
          })
        );
      }

      dispatch(setLastChangedAtAction(Date.now()));
    } catch (error) {
      console.error(error);
    } finally {
      setIsWorking(false);
    }
  };

  const handleSelectFromDetail = () => {
    setIsDetailOpen(false);
    handleSelect();
  };

  return (
    <>
      <div
        className={twMerge(
          "w-24",
          isReadOnly &&
            !isSelected &&
            "transition-all duration-150 opacity-60 hover:opacity-80"
        )}
      >
        <button
          className={twMerge(
            "relative",
            !isReadOnly &&
              "cursor-pointer group transition-all duration-150 hover:scale-105 active:scale-95"
          )}
          onClick={handleSelect}
        >
          <div
            className={twMerge(
              "size-24 rounded-sm border-2 transition-all duration-150 bg-white overflow-hidden",
              "group-hover:border-primaryOrange",
              isSelected ? "border-primaryOrange" : "border-greyLight"
            )}
          >
            {isService ? (
              <ServiceTile index={index} />
            ) : (
              <img
                alt={label}
                className={twMerge(
                  "size-full",
                  isPack ? "object-contain" : "object-cover"
                )}
                src={image}
                title={label}
              />
            )}
          </div>
          <div
            className={twMerge(
              "absolute -right-1 -top-1 size-4 bg-white border rounded-sm group-hover:border-primaryOrange",
              isSelected ? "border-primaryOrange" : "border-greyLight"
            )}
          >
            {isSelected && (
              <div className="bg-primaryOrange size-3 absolute top-1/2 left-1/2 -translate-1/2 rounded-xs" />
            )}
          </div>
        </button>
        <div className="flex flex-col gap-1 text-center">
          <div className="text-black text-xs/4 font-semibold mt-3 whitespace-normal">
            {label}
            <button
              className="shrink-0 grow-0 align-text-bottom ml-1 text-greyDark hover:text-greyLight cursor-pointer"
              onClick={() => setIsDetailOpen(true)}
            >
              <InfoIcon className="size-4" />
            </button>
          </div>
          <div className="text-sm font-semibold">
            <FinancePrice amount={price} />
          </div>
        </div>
      </div>
      <Modal
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        doNotRenderInvisibile
        withCloseIcon
      >
        <UpsellItemDetail
          isReadOnly={isReadOnly}
          isSelectable={!isSelected || isBasePreconfigAvailable}
          isSelected={isSelected}
          item={item}
          label={label}
          onClick={handleSelectFromDetail}
          price={price}
          type={type}
        />
      </Modal>
    </>
  );
};

UpsellCarouselItem.propTypes = {
  index: number,
  item: object,
  isReadOnly: bool,
  isSelected: bool,
  setIsWorking: func,
  type: string,
};

export default UpsellCarouselItem;
